import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import {
  createQueryParams,
  createShouldClause,
  createTermClause,
  type ElasticsearchQueryFilter,
  QUERY_DEFAULTS,
  QUERY_FIELD_NAMES,
} from '@/shared/api/openmetadata';
import type { OpenMetadataResponse } from '@/shared/api/openmetadata';
import { escapeSearchQuery } from '@/shared/lib/sanitize';
import type { SortOption } from '@/shared/types/common';

import type { FilterState } from '@/entities/discovery/domain/types';

const getTagAssetSearchFieldName = (tagFqn: string) => {
  return tagFqn.toLowerCase().startsWith('certification.')
    ? QUERY_FIELD_NAMES.CERTIFICATION_FQN
    : QUERY_FIELD_NAMES.TAGS_FQN;
};

const buildCertificationAssetsQueryFilter = (
  certificationTagFqn: string,
  filters: FilterState
): ElasticsearchQueryFilter => {
  const tagFieldName = getTagAssetSearchFieldName(certificationTagFqn);
  const mustClauses: Array<Record<string, unknown>> = [{ term: { [tagFieldName]: certificationTagFqn } }];

  if (filters.assetType && filters.assetType.length > 0) {
    mustClauses.push(createShouldClause('entityType', filters.assetType));
  }

  if (filters.dataSource && filters.dataSource.length > 0) {
    const serviceTypes = filters.dataSource.map((ds) => ds.split('.')[0]);
    mustClauses.push(createShouldClause('serviceType', serviceTypes));
  }

  if (filters.owner && filters.owner.length > 0) {
    mustClauses.push(createShouldClause('owners.displayName.keyword', filters.owner));
  }

  if (filters.tags && filters.tags.length > 0) {
    mustClauses.push(createShouldClause('tags.tagFQN', filters.tags));
  }

  if (filters.tier && filters.tier.length > 0) {
    mustClauses.push(createShouldClause('tier.tagFQN', filters.tier));
  }

  if (filters.domain && filters.domain.length > 0) {
    mustClauses.push(createShouldClause('domains.name.keyword', filters.domain));
  }

  if (filters.certification && filters.certification.length > 0) {
    mustClauses.push(createShouldClause('certification.tagLabel.tagFQN', filters.certification));
  }

  const mustNotClauses = [createTermClause('entityType', 'dataProduct')];

  return {
    query: {
      bool: {
        must: mustClauses,
        must_not: mustNotClauses,
      },
    },
  };
};

export const searchCertificationAssets = async (
  certificationTagFqn: string,
  filters: FilterState,
  from: number = 0,
  size: number = QUERY_DEFAULTS.SIZE.SEARCH_PAGE,
  query: string = '',
  sortBy: SortOption['field'],
  sortOrder: SortOption['direction']
): Promise<OpenMetadataResponse> => {
  // OpenSearch 특수 문자 이스케이핑 (파싱 에러 방지)
  const escapedQuery = query ? escapeSearchQuery(query) : '';

  const params = createQueryParams({
    q: escapedQuery ? `displayName:*${escapedQuery}* OR description:*${escapedQuery}*` : QUERY_DEFAULTS.Q,
    index: 'all',
    from,
    size,
    deleted: false,
    sort_field: sortBy,
    sort_order: sortOrder,
    exclude_source_fields: 'columns',
  });

  const queryFilter = buildCertificationAssetsQueryFilter(certificationTagFqn, filters);
  params.query_filter = JSON.stringify(queryFilter);

  const response = await openMetadataApiClient.get<OpenMetadataResponse>(`/search/query`, {
    params,
    paramsSerializer: {
      encode: (value) => encodeURIComponent(value),
    },
  });

  return response.data;
};

type CertificationAssetsCountResponse = {
  hits: {
    total: {
      value: number;
    };
  };
};

export const fetchCertificationAssetsCount = async (certificationTagFqn: string): Promise<number> => {
  try {
    const tagFieldName = getTagAssetSearchFieldName(certificationTagFqn);
    const queryFilter = {
      query: {
        bool: {
          must: [{ term: { [tagFieldName]: certificationTagFqn } }],
        },
      },
    };

    const response = await openMetadataApiClient.get<CertificationAssetsCountResponse>('/search/query', {
      params: {
        q: '',
        index: 'all',
        from: 0,
        size: 0,
        deleted: false,
        query_filter: JSON.stringify(queryFilter),
      },
      paramsSerializer: {
        encode: (value) => encodeURIComponent(value),
      },
    });
    return response.data.hits.total.value;
  } catch (error) {
    console.error('Failed to fetch certification assets count:', error);
    return 0;
  }
};
